<script setup lang="ts">
import { useRoute, useRouter } from 'vue-router';
import SmarticoHistory from './History.vue';
import SmarticoItemsMiniGame from './items/MiniGame.vue';
import PerfilOficialSwiper from '@/components/perfil-oficial/Swiper.vue';
import { useSmarticoViewStore } from '~/stores/smartico/view';

const smarticoViewStore = useSmarticoViewStore();
const { mainView } = storeToRefs(smarticoViewStore);
const { miniGames, isLoading } = storeToRefs(useSmarticoMiniGames());
const { loadMiniGames } = useSmarticoMiniGames();
const { isAuthenticated } = storeToRefs(useAuthStore());
const { openLogin } = useAuth();
const showHistory = ref(false);
const historyRef = ref<any>(null);

const { isReady, initialLoadComplete } = useSmarticoUserReady(undefined, [
  'getMiniGames',
]);

// Aguardar usuário e Smartico API estarem prontos antes de carregar mini games
watch(
  isReady,
  async (ready) => {
    if (ready) {
      await loadMiniGames();
      initialLoadComplete.value = true;
    }
  },
  { immediate: true },
);

const gameTypeNames: Record<string, string> = {
  wheel: 'Roletas',
  giftbox: 'Baús',
  matchx: 'Palpites',
  quiz: 'Quiz',
  scratch: 'Raspadinhas',
  coin_flip: 'Coin Flip',
};

const gameTypeIcons: Record<string, string> = {
  wheel: 'home/melhores-roletas',
  giftbox: 'mini-games/bau',
  matchx: 'home/tournament',
  quiz: 'home/mais-jogados',
  scratch: 'home/novidades',
  coin_flip: 'store/coin',
};

const gameTypeOrder = [
  'wheel',
  'giftbox',
  'scratch',
  'coin_flip',
  'matchx',
  'quiz',
];

// Minigames após aplicar exclusões (sem o filtro de tab), usado para decidir
// quais tabs têm conteúdo
const baseFilteredMiniGames = computed(() => {
  return (miniGames.value || [])
    .filter((miniGame: TMiniGameTemplate) => miniGame.custom_section_id !== 717)
    .filter(
      (miniGame: TMiniGameTemplate) =>
        !(
          (miniGame.spin_count ?? 0) === 0 &&
          miniGame.saw_template_ui_definition.disable_background_music === true
        ),
    );
});

const availableGameTypes = computed(() => {
  const set = new Set<string>();

  for (const game of baseFilteredMiniGames.value) {
    if (game.saw_game_type) set.add(game.saw_game_type);
  }

  return set;
});

const ribbonTabs = computed(() => {
  const allTabs = [
    { name: 'Todos', value: 'all' },
    { name: 'Baús', value: 'giftbox' },
    { name: 'Roletas', value: 'wheel' },
    { name: 'Raspadinhas', value: 'scratch' },
    { name: 'Coin Flip', value: 'coin_flip' },
    { name: 'Palpites', value: 'matchx' },
    { name: 'Quiz', value: 'quiz' },
  ];

  // Antes do load inicial terminar, mantém todas visíveis pra evitar flick
  if (!initialLoadComplete.value) return allTabs;

  return allTabs.filter(
    (tab) => tab.value === 'all' || availableGameTypes.value.has(tab.value),
  );
});

// --- Sincronização tab/query param (padrão robusto, sem ciclo) ---
const route = useRoute();
const router = useRouter();

function getTabFromQuery() {
  let tabFromQuery: string | undefined;

  for (const key in route.query) {
    if (key.toLowerCase() === 'tab') {
      tabFromQuery = route.query[key] as string;
      break;
    }
  }
  if (tabFromQuery) {
    const normalizedQuery = normalizeString(tabFromQuery);
    const validTab = ribbonTabs.value.find((tab) => {
      return (
        normalizeString(tab.value) === normalizedQuery ||
        normalizeString(tab.name) === normalizedQuery
      );
    });

    if (validTab) {
      return validTab.value;
    }
  }

  return 'all';
}

const ribbonTab = ref<string>(getTabFromQuery());

// Atualiza ribbonTab se a query mudar (mas não se for igual)
watch(
  () => route.query.tab,
  (newTab) => {
    const normalizedQuery = normalizeString(String(newTab || ''));
    const validTab = ribbonTabs.value.find(
      (tab) =>
        normalizeString(tab.value) === normalizedQuery ||
        normalizeString(tab.name) === normalizedQuery,
    );

    if (validTab && ribbonTab.value !== validTab.value) {
      ribbonTab.value = validTab.value;
    }
    if (!newTab && ribbonTab.value !== 'all') {
      ribbonTab.value = 'all';
    }
  },
);

// Função para trocar a tab via query param (usada no click)
function setTabByQuery(tabValue: string) {
  const tabObj = ribbonTabs.value.find((t) => t.value === tabValue);
  const tabParam = tabObj
    ? normalizeString(tabObj.name)
    : normalizeString(tabValue);

  if (normalizeString(String(route.query.tab || '')) !== tabParam) {
    router.replace({
      query: {
        ...route.query,
        tab: tabParam,
      },
    });
  }
}

const filteredMiniGames = computed(() => {
  let filtered = baseFilteredMiniGames.value;

  if (ribbonTab.value !== 'all') {
    filtered = filtered.filter(
      (miniGame: TMiniGameTemplate) =>
        miniGame.saw_game_type === ribbonTab.value,
    );
  }

  // Ordenar por prioridade
  return [...filtered].sort(
    (a, b) =>
      (a.saw_template_ui_definition.priority ?? 10) -
      (b.saw_template_ui_definition.priority ?? 10),
  );
});

// Minigames disponíveis para jogar (spin_count > 0)
const availableGames = computed(() => {
  return filteredMiniGames.value.filter(
    (miniGame: TMiniGameTemplate) => (miniGame.spin_count ?? 0) > 0,
  );
});

const gridContainerRef = ref<HTMLElement | null>(null);

// Agrupar minigames por tipo de jogo (para tab "Todos")
const gamesGroupedByType = computed(() => {
  const grouped: Record<string, TMiniGameTemplate[]> = {};

  for (const game of filteredMiniGames.value) {
    const type = game.saw_game_type || 'other';

    if (!grouped[type]) {
      grouped[type] = [];
    }
    grouped[type].push(game);
  }

  return grouped;
});

const groupRefs = ref<Record<string, HTMLElement | null>>({});

onBeforeMount(() => {
  mainView.value = 'minigames';
});

// Função utilitária para normalizar strings (remover acentos e deixar minúsculo)
function normalizeString(str: string): string {
  // Remove acentos usando regex compatível e converte para minúsculo
  return str
    .normalize('NFD')
    .replaceAll(/[\u0300-\u036F]/g, '')
    .toLowerCase();
}

// Função para abrir histórico (requer autenticação) - igual StoreV2.vue
async function openHistory() {
  if (!isAuthenticated || !isAuthenticated.value) {
    try {
      openLogin();
    } catch {}

    return;
  }
  showHistory.value = true;
  try {
    await nextTick();
    historyRef.value?.refreshHistory?.();
  } catch (err) {
    console.error('Erro ao carregar histórico:', err);
  }
}

// Função para voltar para minigames
function closeHistory() {
  showHistory.value = false;
}

// Refs para o container de tabs
const tabsRef = ref<HTMLElement | null>(null);
const minigamesRootRef = ref<HTMLElement | null>(null);

// Função para scroll da página para o topo (igual StoreV2.vue)
function scrollToTop() {
  try {
    if (
      typeof window !== 'undefined' &&
      typeof window.scrollTo === 'function'
    ) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  } catch {}
  try {
    if (typeof document !== 'undefined') {
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
  } catch {}
  // Scroll em containers com overflow
  try {
    let node = minigamesRootRef.value as HTMLElement | null;

    while (node) {
      const style = window.getComputedStyle(node);
      const overflowY = style?.overflowY;

      if (
        (overflowY === 'auto' ||
          overflowY === 'scroll' ||
          overflowY === 'overlay') &&
        node.scrollHeight > node.clientHeight
      ) {
        try {
          if (typeof (node as any).scrollTo === 'function') {
            (node as any).scrollTo({ top: 0, behavior: 'smooth' });
          } else {
            (node as any).scrollTop = 0;
          }
        } catch {}
      }
      node = node.parentElement as HTMLElement | null;
    }
  } catch {}
}

// Função para scroll horizontal da tab ativa (igual StoreV2.vue)
function scrollActiveTabIntoView() {
  try {
    const container = tabsRef.value;

    if (!container) return;

    const activeButton = container.querySelector('.activeTab') as HTMLElement;

    if (activeButton) {
      // Calcula a posição para centralizar a tab ativa
      const scrollLeft =
        activeButton.offsetLeft -
        container.offsetWidth / 2 +
        activeButton.offsetWidth / 2;

      container.scrollTo({
        left: Math.max(0, scrollLeft),
        behavior: 'smooth',
      });
    }
  } catch {}
}

// Watch para fazer scroll quando a tab mudar (igual StoreV2.vue)
watch(ribbonTab, () => {
  nextTick(() => {
    scrollToTop();
    scrollActiveTabIntoView();
  });
});

// Se a tab ativa sumir (categoria ficou sem jogos após reload), volta pra "Todos"
watch(ribbonTabs, (newTabs) => {
  if (!newTabs.some((tab) => tab.value === ribbonTab.value)) {
    ribbonTab.value = 'all';
  }
});
</script>

<template>
  <div
    ref="minigamesRootRef"
    :class="'smarticoPageMiniGames'"
  >
    <BaseScreenHeader
      heading-tag="h1"
      title="Mini Games"
    >
      <template #right>
        <button
          v-if="!showHistory"
          aria-label="Atualizar mini games"
          class="actionButton"
          @click="loadMiniGames"
        >
          <NuxtIcon
            aria-hidden="true"
            :class="{ 'animate-spin': isLoading }"
            name="sync"
          />
        </button>
        <button
          :aria-label="showHistory ? 'Ver mini games' : 'Ver histórico'"
          class="actionButton historyBtn md:!hidden"
          @click="showHistory ? closeHistory() : openHistory()"
        >
          <NuxtIcon
            aria-hidden="true"
            :name="showHistory ? 'store/store' : 'mini-games/historico'"
          />
          <span class="historyText">
            {{ showHistory ? 'MINI-GAMES' : 'HISTÓRICO' }}
          </span>
        </button>
      </template>
    </BaseScreenHeader>

    <!-- Container de conteúdo -->
    <div class="minigames-content">
      <!-- Abas de filtro - igual StoreV2.vue -->
      <section class="section">
        <div class="tabs-wrapper">
          <div
            ref="tabsRef"
            class="tabs"
          >
            <BaseFilterTabs
              :model-value="ribbonTab"
              :tabs="ribbonTabs"
              @update:model-value="setTabByQuery($event as string)"
            />
          </div>
          <button
            class="history-button hidden md:!flex h-[41px] w-auto justify-center items-center gap-[15px] border [background:#010612] backdrop-blur-[10px] px-4 py-2 rounded-[10px] border-solid border-[#1D2329] text-white"
            @click="showHistory ? closeHistory() : openHistory()"
          >
            <NuxtIcon
              class="text-[20px] text-white"
              filled
              :name="showHistory ? 'store/store' : 'mini-games/historico'"
            />
            <span class="text-white text-[13px] font-bold leading-4">
              {{ showHistory ? 'VER MINIGAMES' : 'VER HISTÓRICO' }}
            </span>
          </button>
        </div>

        <!-- Histórico de compras - igual StoreV2.vue -->
        <div v-if="showHistory">
          <SmarticoHistory ref="historyRef" />
        </div>

        <!-- Skeleton: grupos na tab "Todos", grid simples nas outras tabs -->
        <template v-else-if="!initialLoadComplete">
          <SmarticoApostatudoSkeletonMiniGamesGroupsSkeleton
            v-if="ribbonTab === 'all'"
            :card-height="217"
            :cards-per-group="4"
            :groups="5"
          />
          <SmarticoApostatudoSkeletonGridSkeleton
            v-else
            :count="8"
            :height="217"
          />
        </template>

        <template v-else>
          <!-- Tab "Todos": Disponíveis primeiro, depois por tipo -->
          <div
            v-if="
              ribbonTab === 'all' && Object.keys(gamesGroupedByType).length > 0
            "
            key="tab-all"
            :ref="(el) => (gridContainerRef = el as HTMLElement)"
          >
            <!-- Seção: Disponíveis -->
            <div
              v-if="availableGames.length > 0"
              class="mb-6"
            >
              <PerfilOficialSwiper
                icon-path="home/ganhos-turbo"
                see-more-url="/vip/mini-games"
                title="Disponíveis"
              >
                <SmarticoItemsMiniGame
                  v-for="minigame in availableGames"
                  :key="minigame.id"
                  class="flex flex-shrink-0"
                  :minigame="minigame"
                />
              </PerfilOficialSwiper>
            </div>

            <!-- Seções por tipo de jogo -->
            <template
              v-for="type in gameTypeOrder"
              :key="type"
            >
              <div
                v-if="(gamesGroupedByType[type]?.length ?? 0) > 0"
                :ref="(el) => (groupRefs[type] = el as HTMLElement)"
                class="mb-6"
              >
                <PerfilOficialSwiper
                  :icon-path="gameTypeIcons[type] || 'home/melhores-roletas'"
                  :see-more-url="`/vip/mini-games?tab=${normalizeString(gameTypeNames[type] || type)}`"
                  :title="gameTypeNames[type] || type"
                >
                  <SmarticoItemsMiniGame
                    v-for="minigame in gamesGroupedByType[type] ?? []"
                    :key="minigame.id"
                    class="flex flex-shrink-0"
                    :minigame="minigame"
                  />
                </PerfilOficialSwiper>
              </div>
            </template>
          </div>

          <!-- Outras tabs: Grid de minigames filtrados -->
          <div
            v-else-if="ribbonTab !== 'all' && filteredMiniGames.length > 0"
            key="tab-filtered"
          >
            <div class="gridItems">
              <SmarticoItemsMiniGame
                v-for="minigame in filteredMiniGames"
                :key="minigame.id"
                :minigame="minigame"
              />
            </div>
          </div>

          <!-- Sem resultados -->
          <div
            v-else
            class="alert alert-warning"
          >
            <span>
              {{ $t('components.search_bar.no_results') }}
            </span>
          </div>
        </template>
      </section>
    </div>

    
  </div>
</template>

<style scoped lang="postcss">
.swiper-button-prev-custom,
.swiper-button-next-custom {
  @apply cursor-pointer;
}

.smarticoPageMiniGames {
  @apply w-full min-h-[560px] mb-10 -mt-3 lg:-mt-1;
}

.actionButton {
  @apply p-2 bg-transparent text-white/60 hover:text-white transition-colors cursor-pointer flex items-center justify-center;

  svg {
    @apply w-5 h-5;
  }
}

.historyBtn {
  @apply flex items-center gap-1;
}

.historyText {
  @apply text-[10px] font-semibold text-white;
}

/* Container de conteúdo */
.minigames-content {
  @apply w-full;
}

.section {
  @apply mb-6;

  /* No mobile, remove margens extras */
  @media (max-width: 767px) {
    @apply mb-4 mt-0 pt-0;
  }
}

.gridItems {
  @apply grid gap-3 overflow-visible;
  grid-template-columns: repeat(auto-fill, minmax(208px, 1fr));
  padding-top: 12px; /* espaço para o timer badge no topo dos cards */
  justify-items: center;

  @media (max-width: 639px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    padding-bottom: 10px; /* evita que a primeira fileira seja cortada em telas pequenas */
  }

  /* Telas muito pequenas: gap menor e cards menores */
  @media (max-width: 450px) {
    gap: 6px;

    /* Reduzir cards dentro do grid em telas pequenas */
    :deep(.card-outer-wrapper) {
      width: 100%;
    }

    :deep(.minigame-card) {
      width: 100%;
      max-width: 180px;
      height: auto;
      min-height: 180px;
      padding: 10px;
      gap: 5px;
    }

    :deep(.card-image) {
      width: 65px;
      height: 65px;
    }

    :deep(.card-title) {
      font-size: 14px;
      line-height: 15px;
      min-height: 15px;
    }

    :deep(.card-description) {
      font-size: 12px;
      line-height: 14px;
      -webkit-line-clamp: 1;
      line-clamp: 1;
      min-height: 14px;
    }

    :deep(.card-actions) {
      gap: 4px;
      height: 30px;
    }

    :deep(.price-badge) {
      padding: 4px 6px;
      gap: 3px;
    }

    :deep(.price-icon) {
      width: 16px;
      height: 16px;
    }

    :deep(.price-value) {
      font-size: 12px;
      line-height: 12px;
    }

    :deep(.price-name) {
      font-size: 8px;
      line-height: 8px;
    }

    :deep(.price-free-text) {
      font-size: 10px;
      line-height: 10px;
    }

    :deep(.open-button) {
      padding: 4px;
      gap: 3px;
      font-size: 10px;
      line-height: 12px;
    }

    :deep(.timer-badge) {
      height: 18px;
      padding: 0 5px;
      gap: 2px;
    }

    :deep(.timer-icon) {
      font-size: 11px;
      width: 11px;
      height: 11px;
    }

    :deep(.timer-text) {
      font-size: 9px;
      line-height: 8px;
    }
  }
}

.gridItems > * {
  animation: shuffle-card 0.7s ease-in-out;
  animation-fill-mode: forwards;
}

@keyframes shuffle-card {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Tabs wrapper - igual StoreV2.vue */
.tabs-wrapper {
  @apply flex justify-between items-center mb-4 border-b border-[#424344] gap-[5px];
  position: relative;

  /* No mobile, remove margem superior extra */
  @media (max-width: 767px) {
    @apply mt-0;
  }
}

/* Tabs - igual StoreV2.vue */
.tabs {
  @apply flex justify-center overflow-x-auto flex-1;
  gap: 7.5px;

  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none;
  scrollbar-width: none;

  /* No desktop, alinha à esquerda */
  @media (min-width: 1024px) {
    @apply justify-start;
  }
}

/* History button - igual StoreV2.vue */
.history-button {
  @apply flex-shrink-0;
}
</style>
